/**
 * In-game chat — bottom-left panel that lets players exchange messages.
 *
 *   • Global messages by default (everyone sees them).
 *   • Private messages by selecting a target via the strip's right-click
 *     "Discuter" entry — sender + target only.
 *   • Each incoming message animates the speaker's army-head sprite with
 *     a brief wobble, types out the text character-by-character, and
 *     plays a sound (subtle for global, louder for private DMs).
 *
 * The panel is collapsible (header click) so it doesn't eat screen space
 * permanently. State (open/closed, current private-target, message log)
 * lives at module scope so the panel survives HUD re-renders.
 */

import type { Room } from '../lobby/colyseus.js';
import type { Participant } from '../lobby/colyseus.js';
import { playTinkle, playFanfare } from '../sound/sounds.js';

export interface ChatMessage {
  senderId: string;
  senderName: string;
  senderFactionId: string;
  targetId: string | null;
  text: string;
  timestamp: number;
}

const MAX_MESSAGES = 80;
const messages: ChatMessage[] = [];

let panelOpen = true;
/** Number of incoming messages received while the panel was closed.
 *  Rendered as a chip on the closed (badge) header so mobile players
 *  can glance and see they have new chat. Reset when the panel opens. */
let unreadCount = 0;
let privateTargetId: string | null = null;
let privateTargetName: string | null = null;
let myUserId: string | null = null;
let chatRoom: Room | null = null;
let subscribed = false;

/** True when the active viewport is narrow enough to use the mobile
 *  badge + popup layout. Same breakpoint as `isMobileViewport()` in
 *  in_game.ts so the two stay aligned. */
function isMobileChatViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

/** Active "is typing" peer (private only). `null` when no one is typing. */
let typingFromId: string | null = null;
let typingFromName: string | null = null;
let typingExpiresAt = 0;
let typingTickHandle: number | null = null;

/** Throttle outbound chat.typing so a user mashing keys doesn't flood. */
let lastTypingSentAt = 0;

/**
 * The chat panel is a PERSISTENT DOM node attached directly to <body>.
 * Living outside the in-game HUD means the chat survives every full
 * `renderHud()` rebuild (which wipes `host.innerHTML`) — no more "tilt"
 * / flicker every time you click a territory or trigger any state patch.
 */
let chatHost: HTMLElement | null = null;

/** Keys of messages that have already had their wobble + typewriter played.
 *  Any subsequent re-render (e.g. HUD rebuild after a territory click)
 *  finds the key here and skips animation — the message renders statically.
 *  Cleared on detach so a new game starts fresh. */
const animatedMsgKeys = new Set<string>();
function msgKey(m: ChatMessage): string {
  return `${m.timestamp}-${m.senderId}-${m.text.length}`;
}

/**
 * Wire the chat into the current room. Idempotent — calling on each HUD
 * mount is safe; the message listener is reattached once. New incoming
 * messages append to the log + flag the active panel to re-render.
 */
export function attachChat(room: Room, myId: string): void {
  myUserId = myId;
  chatRoom = room;
  // Initial panel state depends on viewport: open on desktop, closed
  // (badge mode) on mobile so the chat doesn't eat half the screen.
  // Only set on first attach — subsequent re-attaches preserve whatever
  // the player has toggled to during the session.
  if (!chatHost) {
    panelOpen = !isMobileChatViewport();
    unreadCount = 0;
    chatHost = document.createElement('div');
    chatHost.id = 'gh-chat-host';
    chatHost.className = 'gh-chat-host';
    document.body.appendChild(chatHost);
    renderChatInto(chatHost);
  }
  if (subscribed) return;
  subscribed = true;
  room.onMessage('chat.message', (msg: ChatMessage) => {
    messages.push(msg);
    if (messages.length > MAX_MESSAGES) messages.shift();
    const isPrivate = !!msg.targetId;
    // Skip sound when I'm the sender (already heard the click).
    if (msg.senderId !== myUserId) {
      if (isPrivate) playFanfare(); // louder cue — DM is important
      else playTinkle();             // subtle global ping
      // Bump the unread counter whenever a peer's message arrives
      // while the panel is collapsed. Self-messages don't count.
      if (!panelOpen) unreadCount += 1;
    }
    // Clear typing indicator if the typer just sent the actual message.
    if (typingFromId && msg.senderId === typingFromId) {
      typingFromId = null;
      typingFromName = null;
      typingExpiresAt = 0;
    }
    // Refresh the panel if it's mounted.
    const host = document.getElementById('gh-chat-host');
    if (host) renderChatInto(host);
  });

  // "Gratte à la porte" — a peer is typing a private message to me.
  // The indicator shows for 4 s after the last typing event; we set
  // typingExpiresAt and a ticker re-renders to fade it out cleanly.
  room.onMessage('chat.typing', (payload: { senderId: string; senderName: string }) => {
    typingFromId = payload.senderId;
    typingFromName = payload.senderName;
    typingExpiresAt = performance.now() + 4000;
    const host = document.getElementById('gh-chat-host');
    if (host) renderChatInto(host);
    // Schedule expiry tick — only one outstanding at a time.
    if (typingTickHandle !== null) window.clearTimeout(typingTickHandle);
    typingTickHandle = window.setTimeout(() => {
      typingTickHandle = null;
      // Decide if it has expired vs still relevant.
      if (performance.now() >= typingExpiresAt) {
        typingFromId = null;
        typingFromName = null;
        const h = document.getElementById('gh-chat-host');
        if (h) renderChatInto(h);
      }
    }, 4100);
  });
}

/** Detach + reset state — call from unmount-in-game. */
export function detachChat(): void {
  chatRoom = null;
  myUserId = null;
  subscribed = false;
  messages.length = 0;
  animatedMsgKeys.clear();
  privateTargetId = null;
  privateTargetName = null;
  unreadCount = 0;
  if (chatHost) {
    chatHost.remove();
    chatHost = null;
  }
}

/** Open a private channel with the given participant. Called from the
 *  strip's right-click "Discuter" menu. */
export function openPrivateChannel(p: Participant): void {
  privateTargetId = p.id;
  privateTargetName = p.name;
  panelOpen = true;
  unreadCount = 0;
  const host = document.getElementById('gh-chat-host');
  if (host) renderChatInto(host);
}

/**
 * Legacy no-op preserved for older call sites. The chat panel now manages
 * its own DOM node attached to <body> in `attachChat()`. Returns a
 * detached placeholder so any leftover `host.appendChild(renderChat())`
 * calls don't blow up — but they're cosmetic only.
 */
export function renderChat(): HTMLElement {
  return document.createElement('div');
}

function renderChatInto(host: HTMLElement): void {
  host.innerHTML = '';
  host.classList.toggle('gh-chat-closed', !panelOpen);
  // Tag the host with the current viewport mode so CSS can swap the
  // closed state between "header strip" (desktop) and "circular badge"
  // (mobile). Re-evaluated on every render so the user resizing a
  // window flips the layout immediately.
  host.classList.toggle('gh-chat-mobile', isMobileChatViewport());

  // Header — title + collapse toggle + (when active) private-channel chip.
  const header = document.createElement('div');
  header.className = 'gh-chat-header';
  const headerTitle = document.createElement('span');
  headerTitle.className = 'gh-chat-header-title';
  const titleIcon = document.createElement('img');
  titleIcon.src = '/sprites/iconchat_crier.png';
  titleIcon.className = 'gh-chat-header-icon';
  titleIcon.alt = '';
  headerTitle.appendChild(titleIcon);
  headerTitle.appendChild(document.createTextNode('Miaou·chat'));
  // Unread-message chip — visible only when the panel is collapsed AND
  // we received messages while it was closed. Tap the header (or in
  // mobile badge mode, the whole host) to open the chat and zero it.
  if (!panelOpen && unreadCount > 0) {
    const chip = document.createElement('span');
    chip.className = 'gh-chat-unread-badge';
    chip.textContent = unreadCount > 9 ? '9+' : String(unreadCount);
    headerTitle.appendChild(chip);
  }
  header.appendChild(headerTitle);
  if (privateTargetId && privateTargetName) {
    const chip = document.createElement('span');
    chip.className = 'gh-chat-private-chip';
    chip.textContent = `🔒 ${privateTargetName}`;
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gh-chat-private-close';
    closeBtn.title = 'Quitter le canal privé';
    const closeImg = document.createElement('img');
    closeImg.src = '/sprites/ui_fermer.png';
    closeImg.className = 'gh-chat-private-close-icon';
    closeImg.alt = 'Fermer';
    closeBtn.appendChild(closeImg);
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      privateTargetId = null;
      privateTargetName = null;
      renderChatInto(host);
    });
    chip.appendChild(closeBtn);
    header.appendChild(chip);
  }
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'gh-chat-toggle';
  toggle.textContent = panelOpen ? '▾' : '▸';
  toggle.title = panelOpen ? 'Réduire' : 'Ouvrir';
  header.appendChild(toggle);
  header.addEventListener('click', (e) => {
    // Stop the click from continuing up to the host — without this, on
    // mobile the SAME click event would bubble to a freshly-registered
    // host listener (added below when re-rendering in the closed state)
    // and immediately re-open the chat, leaving the user unable to
    // close it.
    e.stopPropagation();
    panelOpen = !panelOpen;
    if (panelOpen) unreadCount = 0;
    renderChatInto(host);
  });
  host.appendChild(header);

  if (!panelOpen) return;

  // Message log
  const log = document.createElement('div');
  log.className = 'gh-chat-log';
  for (const msg of messages) {
    log.appendChild(buildMessageEl(msg));
  }
  host.appendChild(log);
  // Scroll to bottom on render — newest message visible.
  setTimeout(() => { log.scrollTop = log.scrollHeight; }, 0);

  // "Gratte à la porte" — show a typing indicator above the input
  // whenever a peer is sending me a private DM. Hidden when the indicator
  // has expired (4 s without further keystrokes from the peer) OR when
  // the indicator is from someone other than my current private channel
  // (no surprise indicators while I'm reading global chat).
  if (
    typingFromId &&
    typingFromName &&
    privateTargetId === typingFromId &&
    performance.now() < typingExpiresAt
  ) {
    const indicator = document.createElement('div');
    indicator.className = 'gh-chat-typing';
    const paw = document.createElement('span');
    paw.className = 'gh-chat-typing-paw';
    paw.textContent = '🐾';
    indicator.appendChild(paw);
    const text = document.createElement('span');
    text.className = 'gh-chat-typing-text';
    text.textContent = `${typingFromName} gratte à la porte…`;
    indicator.appendChild(text);
    host.appendChild(indicator);
  }

  // Input
  const inputRow = document.createElement('form');
  inputRow.className = 'gh-chat-input';
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = privateTargetId
    ? `Miauler à ${privateTargetName ?? 'lui'}…`
    : 'Miauler à tout le monde…';
  input.maxLength = 240;
  inputRow.appendChild(input);
  const sendBtn = document.createElement('button');
  sendBtn.type = 'submit';
  sendBtn.className = 'gh-chat-send';
  // iconchat_valide image instead of a paw glyph + faction-coloured
  // background to match the "Aller faire la sieste" end-turn button.
  const sendIcon = document.createElement('img');
  sendIcon.src = '/sprites/iconchat_valide.png';
  sendIcon.className = 'gh-chat-send-icon';
  sendIcon.alt = '';
  sendBtn.appendChild(sendIcon);
  sendBtn.title = 'Envoyer';
  inputRow.appendChild(sendBtn);
  inputRow.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || !chatRoom) return;
    chatRoom.send('chat.send', { text, targetId: privateTargetId });
    input.value = '';
  });
  // Stop click on the input from triggering the header collapse.
  inputRow.addEventListener('click', (e) => e.stopPropagation());
  // Emit a typing ping while the user is composing a private DM. Throttled
  // to one send per 1.5 s so a fast typer doesn't flood the server.
  input.addEventListener('input', () => {
    if (!chatRoom || !privateTargetId) return;
    if (input.value.length === 0) return;
    const now = performance.now();
    if (now - lastTypingSentAt < 1500) return;
    lastTypingSentAt = now;
    try { chatRoom.send('chat.typing', { targetId: privateTargetId }); }
    catch { /* room closed */ }
  });
  host.appendChild(inputRow);
}

/**
 * Build a single message element with the speaker's army-head sprite, the
 * sender name, and the typewritered text. The wobble + typewriter only
 * play for the LATEST message at mount time so older messages don't
 * re-animate every re-render.
 */
function buildMessageEl(msg: ChatMessage): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'gh-chat-msg';
  if (msg.targetId) wrap.classList.add('gh-chat-msg-private');
  const isLatest = msg === messages[messages.length - 1];
  const key = msgKey(msg);
  // A message animates ONCE — on the render right after it arrives. Any
  // subsequent re-render (HUD rebuild on territory click, panel collapse
  // toggle, etc.) finds its key in the set and renders it statically.
  const shouldAnimate = isLatest && !animatedMsgKeys.has(key);
  if (shouldAnimate) {
    wrap.classList.add('gh-chat-msg-new');
    animatedMsgKeys.add(key);
  }

  const head = document.createElement('img');
  head.src = msg.senderFactionId
    ? `/sprites/${msg.senderFactionId}_army.png`
    : '/sprites/iconchat_neutre.png';
  head.className = 'gh-chat-head';
  head.alt = '';
  wrap.appendChild(head);

  const body = document.createElement('div');
  body.className = 'gh-chat-msg-body';
  const nameEl = document.createElement('div');
  nameEl.className = 'gh-chat-msg-name';
  nameEl.textContent = msg.targetId ? `${msg.senderName} (privé)` : msg.senderName;
  body.appendChild(nameEl);
  const textEl = document.createElement('div');
  textEl.className = 'gh-chat-msg-text';
  body.appendChild(textEl);
  wrap.appendChild(body);

  // Typewriter only for messages that haven't been animated yet. Already
  // seen messages render their text immediately.
  if (shouldAnimate && msg.text.length > 0) {
    let i = 0;
    const tick = (): void => {
      // If the panel re-rendered (e.g. a newer message arrived), this
      // element is detached — bail out silently.
      if (!textEl.isConnected) return;
      i++;
      textEl.textContent = msg.text.slice(0, i);
      if (i < msg.text.length) window.setTimeout(tick, 18);
    };
    window.setTimeout(tick, 18);
  } else {
    textEl.textContent = msg.text;
  }

  return wrap;
}
